import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../services/firebase.js';
import { getUploadedImages, uploadPageImage, deletePageImage } from '../../services/companyService.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase.js';
import AdminLayout from './AdminLayout.js';
import LoadingSpinner from '../Common/LoadingSpinner.js';

const ImageArchive = () => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [company, setCompany] = useState(null);
    const user = auth.currentUser;

    useEffect(() => {
        const fetchCompany = async () => {
            if (user?.email) {
                try {
                    console.log("Fetching company with email:", user.email);
                    const companiesRef = collection(db, 'companies');
                    const q = query(companiesRef, where('companyEmail', '==', user.email));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        const companyData = { 
                            id: querySnapshot.docs[0].id, 
                            ...querySnapshot.docs[0].data() 
                        };
                        console.log("Found company:", companyData);
                        setCompany(companyData);
                    } else {
                        console.log("No company found with email:", user.email);
                    }
                } catch (err) {
                    console.error('Error fetching company:', err);
                    setError('Kunne ikke hente firma information');
                }
            }
        };
        fetchCompany();
    }, [user]);
    
    const fetchImages = useCallback(async () => {
        if (!company?.cvr) return;
        
        try {
            const fetchedImages = await getUploadedImages(company.cvr);
            setImages(fetchedImages);
        } catch (err) {
            setError('Kunne ikke hente billeder');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [company]);

    useEffect(() => {
        if (company) {
            fetchImages();
        }
    }, [fetchImages, company]);

    useEffect(() => {
        fetchImages();
    }, [fetchImages]);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (images.length >= 15) {
            setError('Du kan ikke uploade flere end 15 billeder');
            return;
        }

        setUploading(true);
        try {
            await uploadPageImage(file, company.cvr);
            await fetchImages(); // Refresh image list
            setError(null);
        } catch (err) {
            setError('Kunne ikke uploade billedet');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (image) => {
        if (!window.confirm('Er du sikker på at du vil slette dette billede? Dette kan ikke fortrydes.')) {
            return;
        }

        setUploading(true);
        try {
            await deletePageImage(image.path, company.cvr);
            await fetchImages(); // Refresh image list
            setError(null);
        } catch (err) {
            setError('Kunne ikke slette billedet');
        } finally {
            setUploading(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-semibold font-montserrat text-gray-900">Billedearkiv</h1>
                    <div className="flex items-center space-x-4">
                        {images.length < 15 && (
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleUpload}
                                disabled={uploading}
                                className="block text-sm text-gray-500
                                    file:mr-4 file:py-2 file:px-4
                                    file:rounded-md file:border-0
                                    file:text-sm file:font-semibold
                                    file:bg-brand-light/20 file:text-brand-dark
                                    hover:file:bg-brand-light/30"
                            />
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {images.length >= 15 && (
                    <div className="mb-4 p-4 bg-brand-light/20 text-brand-dark rounded-md">
                        Du har nået grænsen på 15 billeder. Slet nogle billeder for at uploade nye.
                    </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image) => (
                        <div key={image.path} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden hover:border-brand-dark focus:outline-none focus:ring-2 focus:ring-brand-dark">
                                <img 
                                    src={image.url} 
                                    alt={image.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <button
                                onClick={() => handleDelete(image)}
                                className="absolute top-2 right-2 p-1 bg-red-600 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Slet billede"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </AdminLayout>
    );
};

export default ImageArchive;